import { TRAVEL_PACKAGES_TEXT as T } from "./package-text";
import { formatAppPrice } from "./pricing";
export const TIER_META = {
    basic: {
        key: "simple_trips_count",
        label: T.tiers.basic.label,
        color: "#5F8067",
        accentColor: "#4D6E57",
        softBackground: "#EEF5F1",
        borderColor: "rgba(95,128,103,0.34)",
        subtitle: T.tiers.basic.subtitle,
        bundleLabel: "базовых",
    },
    advanced: {
        key: "advanced_trips_count",
        label: T.tiers.advanced.label,
        color: "#846F5E",
        accentColor: "#846F5E",
        softBackground: "#FAF3EC",
        borderColor: "rgba(132,111,94,0.40)",
        subtitle: T.tiers.advanced.subtitle,
        bundleLabel: "продвинутых",
    },
    pro: {
        key: "pro_trips_count",
        label: T.tiers.pro.label,
        color: "#626778",
        accentColor: "#626778",
        softBackground: "#F1F2F5",
        borderColor: "rgba(98,103,120,0.38)",
        subtitle: T.tiers.pro.subtitle,
        bundleLabel: "профи",
    },
};
export const normalizeCount = (value) => {
    const num = Number(value);
    if (!Number.isFinite(num))
        return 0;
    return Math.max(0, Math.floor(num));
};
export const packageMatchesPreferredTier = (pkg, preferredTier) => {
    if (!preferredTier || !TIER_META[preferredTier])
        return false;
    return normalizeCount(pkg === null || pkg === void 0 ? void 0 : pkg[TIER_META[preferredTier].key]) > 0;
};
export const getPackageTierKey = (pkg) => {
    if (normalizeCount(pkg === null || pkg === void 0 ? void 0 : pkg.simple_trips_count) > 0)
        return "basic";
    if (normalizeCount(pkg === null || pkg === void 0 ? void 0 : pkg.advanced_trips_count) > 0)
        return "advanced";
    if (normalizeCount(pkg === null || pkg === void 0 ? void 0 : pkg.pro_trips_count) > 0)
        return "pro";
    return "";
};
export const getPackageTripsCount = (pkg) => normalizeCount(pkg === null || pkg === void 0 ? void 0 : pkg.simple_trips_count) +
    normalizeCount(pkg === null || pkg === void 0 ? void 0 : pkg.advanced_trips_count) +
    normalizeCount(pkg === null || pkg === void 0 ? void 0 : pkg.pro_trips_count);
export const getTripsLabel = (count) => {
    const normalizedCount = Math.abs(normalizeCount(count));
    const lastTwoDigits = normalizedCount % 100;
    const lastDigit = normalizedCount % 10;
    if (lastTwoDigits >= 11 && lastTwoDigits <= 14)
        return T.tripWords.many;
    if (lastDigit === 1)
        return T.tripWords.one;
    if (lastDigit >= 2 && lastDigit <= 4)
        return T.tripWords.few;
    return T.tripWords.many;
};
export const getPerTripPrice = (pkg) => {
    const responsePricePerTrip = Number(pkg === null || pkg === void 0 ? void 0 : pkg.price_per_trip);
    if (Number.isFinite(responsePricePerTrip))
        return responsePricePerTrip;
    const totalTrips = getPackageTripsCount(pkg);
    const totalPrice = Number(pkg === null || pkg === void 0 ? void 0 : pkg.price);
    if (!Number.isFinite(totalPrice) || totalTrips <= 0)
        return null;
    return totalPrice / totalTrips;
};
export const formatPerTripPrice = (value) => {
    const num = Number(value);
    if (!Number.isFinite(num))
        return formatAppPrice(value);
    return formatAppPrice(Math.round(num));
};
export const getPackageSavings = (currentPkg, previousPkg) => {
    const currentPrice = Number(currentPkg === null || currentPkg === void 0 ? void 0 : currentPkg.price);
    const currentTrips = getPackageTripsCount(currentPkg);
    const previousPerTripPrice = getPerTripPrice(previousPkg);
    if (!Number.isFinite(currentPrice) || currentTrips <= 0 || !Number.isFinite(previousPerTripPrice)) {
        return 0;
    }
    const savings = Math.round(previousPerTripPrice * currentTrips - currentPrice);
    return savings > 0 ? savings : 0;
};
export const getPackageDisplayName = (pkg, variant = "default") => {
    const totalTrips = getPackageTripsCount(pkg);
    if (variant === "count" && totalTrips > 0) {
        return `+${totalTrips} ${getTripsLabel(totalTrips)}`;
    }
    return (pkg === null || pkg === void 0 ? void 0 : pkg.name) || T.package;
};
export const getPackageLimitsFeatureList = (pkg, tierKey = "") => {
    const limits = (pkg === null || pkg === void 0 ? void 0 : pkg.limits) || {};
    const activeTierKey = tierKey || getPackageTierKey(pkg);
    const features = [];
    if (Number(limits === null || limits === void 0 ? void 0 : limits.plan_items_limit) > 0) {
        features.push(`${limits.plan_items_limit} ${T.features.planItems}`);
    }
    if (Number(limits === null || limits === void 0 ? void 0 : limits.points_limit) > 0) {
        features.push(`${limits.points_limit} ${T.features.points}`);
    }
    if (Number(limits === null || limits === void 0 ? void 0 : limits.participant_limit) > 0) {
        const participantCount = Number(limits.participant_limit);
        const participantLastTwoDigits = participantCount % 100;
        const participantLastDigit = participantCount % 10;
        const participantLabel = participantLastDigit === 1 && participantLastTwoDigits !== 11
            ? T.features.participant
            : participantLastDigit >= 2 &&
                participantLastDigit <= 4 &&
                (participantLastTwoDigits < 12 || participantLastTwoDigits > 14)
                ? T.features.participantsFew
                : T.features.participants;
        features.push(`${limits.participant_limit} ${participantLabel}`);
    }
    if (Number(limits === null || limits === void 0 ? void 0 : limits.file_limit) > 0) {
        features.push(`${limits.file_limit} ${T.features.files}`);
    }
    if (Number(limits === null || limits === void 0 ? void 0 : limits.gpx_tracks_limit) > 0) {
        const gpxTrackLabel = Number(limits.gpx_tracks_limit) === 1
            ? T.features.gpxTrack
            : T.features.gpxTracks;
        features.push(`${limits.gpx_tracks_limit} ${gpxTrackLabel}`);
    }
    if (Number(limits === null || limits === void 0 ? void 0 : limits.finance_limit) > 0) {
        features.push(`${limits.finance_limit} ${T.features.financeRecords}`);
    }
    if (Number(limits === null || limits === void 0 ? void 0 : limits.ai_tokens_limit) > 0) {
        const rawAiTokens = Math.max(Number(limits.ai_tokens_limit) || 0, 0);
        const aiCredits = Math.ceil(rawAiTokens / 1500 / 10) * 10;
        features.push(`${T.features.aiLimit}: ${aiCredits.toLocaleString("ru-RU")} ${T.features.credits}`);
    }
    if (activeTierKey === "advanced" || activeTierKey === "pro") {
        features.push(T.features.noAds);
        features.push(T.features.routeAnimation);
        features.push(T.features.map3d);
    }
    if (activeTierKey === "pro") {
        features.push(T.features.collaboration);
        features.push(T.features.pointPhotos);
        features.push(T.features.exportPdf);
    }
    return features;
};
export const getOptionButtonText = () => T.buy;
export const getBundleBreakdownItems = (pkg) => Object.values(TIER_META)
    .map((meta) => {
    const count = normalizeCount(pkg === null || pkg === void 0 ? void 0 : pkg[meta.key]);
    if (count <= 0)
        return null;
    return {
        key: meta.key,
        label: meta.label,
        bundleLabel: meta.bundleLabel,
        count,
        color: meta.color,
        accentColor: meta.accentColor,
        softBackground: meta.softBackground,
        borderColor: meta.borderColor,
    };
})
    .filter((item) => Boolean(item));
export const getSortedPackagesForComparison = (packages = []) => [...packages].sort((a, b) => {
    const countDiff = getPackageTripsCount(a) - getPackageTripsCount(b);
    if (countDiff !== 0)
        return countDiff;
    const aPrice = Number(a === null || a === void 0 ? void 0 : a.price);
    const bPrice = Number(b === null || b === void 0 ? void 0 : b.price);
    if (Number.isFinite(aPrice) && Number.isFinite(bPrice) && aPrice !== bPrice) {
        return aPrice - bPrice;
    }
    const aSort = Number(a === null || a === void 0 ? void 0 : a.sort_order);
    const bSort = Number(b === null || b === void 0 ? void 0 : b.sort_order);
    if (Number.isFinite(aSort) && Number.isFinite(bSort) && aSort !== bSort) {
        return aSort - bSort;
    }
    return String((a === null || a === void 0 ? void 0 : a.name) || "").localeCompare(String((b === null || b === void 0 ? void 0 : b.name) || ""), "ru");
});
export const getCheapestStandalonePriceForCount = (requiredCount, packages = []) => {
    const normalizedRequiredCount = normalizeCount(requiredCount);
    if (normalizedRequiredCount <= 0)
        return 0;
    const options = packages
        .map((pkg) => ({
        count: getPackageTripsCount(pkg),
        price: Number(pkg === null || pkg === void 0 ? void 0 : pkg.price),
    }))
        .filter((option) => option.count > 0 && Number.isFinite(option.price));
    if (options.length === 0)
        return null;
    const maxOptionCount = options.reduce((max, option) => Math.max(max, option.count), 0);
    const searchLimit = normalizedRequiredCount + maxOptionCount * 10;
    const dp = Array(searchLimit + 1).fill(Infinity);
    dp[0] = 0;
    for (let count = 0; count <= searchLimit; count += 1) {
        if (!Number.isFinite(dp[count]))
            continue;
        options.forEach((option) => {
            const nextCount = count + option.count;
            if (nextCount > searchLimit)
                return;
            dp[nextCount] = Math.min(dp[nextCount], dp[count] + option.price);
        });
    }
    if (Number.isFinite(dp[normalizedRequiredCount])) {
        return dp[normalizedRequiredCount];
    }
    for (let count = normalizedRequiredCount + 1; count <= searchLimit; count += 1) {
        if (Number.isFinite(dp[count]))
            return dp[count];
    }
    return null;
};
export const getBundleSavingsFromStandalonePackages = (bundlePkg, standalonePackages = []) => {
    const bundlePrice = Number(bundlePkg === null || bundlePkg === void 0 ? void 0 : bundlePkg.price);
    if (!Number.isFinite(bundlePrice) || !Array.isArray(standalonePackages) || standalonePackages.length === 0) {
        return 0;
    }
    const referenceTotal = Object.entries(TIER_META).reduce((sum, [tierKey, meta]) => {
        if (sum === null)
            return null;
        const tierCount = normalizeCount(bundlePkg === null || bundlePkg === void 0 ? void 0 : bundlePkg[meta.key]);
        if (tierCount <= 0)
            return sum;
        const tierPackages = standalonePackages.filter((pkg) => getPackageTierKey(pkg) === tierKey);
        const tierTotal = getCheapestStandalonePriceForCount(tierCount, tierPackages);
        if (!Number.isFinite(tierTotal))
            return null;
        return sum + tierTotal;
    }, 0);
    if (!Number.isFinite(referenceTotal))
        return 0;
    const savings = Math.round(referenceTotal - bundlePrice);
    return savings > 0 ? savings : 0;
};
